import { Injectable, NgZone } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { 
  AsmtRunnerSend, 
  AsmtRunnerReceive, 
  ExtensionStatus, 
  AssessmentLockState 
} from '../../app/constants/assessment-lock-signals';
import { AuthService } from '../api/auth.service';
import { RoutesService } from '../api/routes.service';
import { VerifiedAccessDebugService } from './verified-access-debug.service';

export const ASSESSMENT_LOCK_UI_SLUG = {
    alLoginIndicator: 'asmt_lock_signed_in', 
    activateAssessmentLock: 'asmt_lock_activate_btn',
    connecting: 'asmt_lock_connecting', 
    extensionNotDetected: 'asmt_lock_ext_not_detected',
};

/**
 * Assessment Lock Service
 * 
 * Manages communication with the Vretta Assessment Lock Chrome Extension.
 * Handles lockdown state, ping/pong heartbeat, and automatic error recovery.
 * 
 * Logged Events (for monitoring and debugging):
 * - ASSESSMENT_LOCK_CONTEXT_INVALIDATED: Extension context became invalid (e.g., extension updated/reloaded)
 * - ASSESSMENT_LOCK_EXTENSION_TIMEOUT: Extension stopped responding to pings during active lock
 * - ASSESSMENT_LOCK_UNEXPECTED_DEACTIVATION: Lock deactivated unexpectedly while app expected it active
 * - ASSESSMENT_LOCK_ACTIVATION_FAILED: Failed to activate assessment lock
 * - ASSESSMENT_LOCK_DEACTIVATION_FAILED: Failed to deactivate assessment lock
 * 
 * Recovery Behavior:
 * - If extension becomes unavailable during active lock, page automatically reloads
 * - If context is invalidated during active lock, page automatically reloads
 */
@Injectable({
  providedIn: 'root'
})
export class AssessmentLockService {
  private readonly PING_INTERVAL = 30000; // 30 seconds
  private readonly PONG_TIMEOUT = 5000;   // 5 seconds
  private readonly VERIFIED_ACCESS_SIGNING_STATUS_TIMEOUT = 1500; // signing availability check timeout
  private readonly DEVICE_TRUST_REUSE_BUFFER_SECONDS = 15; // safety buffer to prevent close-to-expiry token reuse

  // State management
  private stateSubject = new BehaviorSubject<AssessmentLockState>({
    isLockdown: false,
    triggerUrl: null,
    triggerTabId: null,
    previousTabIds: [],
    extensionStatus: ExtensionStatus.CHECKING
  });

  public state$: Observable<AssessmentLockState> = this.stateSubject.asObservable();

  // Internal tracking
  private pingIntervalId: number | null = null;
  private pongTimeoutId: number | null = null;
  private isFirstPongReceived = false;
  private isDisallowed = false;
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private isInitialized = false;
  private supervisorPassword:string;
  public isActive = false;
  public AL_LOGIN_LOCAL_STORAGE_KEY = 'asmt_lock_login';

  /**
   * Latched when student login URL contains `?assessment-lock=`
   * Persists for SPA navigation until cleared explicitly (not tied to AuthService.logout).
   */
  private assessmentLockLaunchFromLoginQuery = false;

  // Map pending requestIDs to promises
  private verifiedAccessRequests = new Map<string, { resolve: (value: string) => void; reject: (reason?: any) => void; timeoutId: number }>();
  private verifiedAccessSigningStatusRequests = new Map<string, { resolve: (value: any) => void; reject: (reason?: any) => void; timeoutId: number }>();
  // Cached managed kiosk proof
  private deviceTrustToken: string | null = null;
  private deviceTrustMode: string | null = null;
  private deviceTrustTestSessionId: number | string | null = null;
  private deviceTrustUid: number | null = null;
  private deviceTrustTokenIssuedAtMs: number | null = null;
  private deviceTrustTokenExpiresAtMs: number | null = null;

  constructor(
    private auth: AuthService,
    private routes: RoutesService,
    private ngZone: NgZone,
    private verifiedAccessDebug: VerifiedAccessDebugService
  ) {
    // Don't auto-initialize - let components control when to start
  }

  /**
   * Initialize the service and start monitoring for extension
   * Call this when you need to start checking for the extension
   */
  init(): void {
    if (this.isInitialized) {
      return;
    }
    this.initializeMessageListener();
    this.startPingInterval();
    this.isInitialized = true;
  }

  ensureChrombookAllow(isNonChrombookAllowed: boolean): void {
    if (!this.isChromebook() && !this.isFirstPongReceived) {
      this.isDisallowed = true
    }
  }

  setSupervisorPassword(supervisorPassword: string): void {
    this.supervisorPassword = supervisorPassword;
  }

  /**
   * Read `assessment-lock=` from a route snapshot (e.g. student login screens).
   * Once true, stays true until {@link clearAssessmentLockLaunchFromLoginQuery}.
   */
  captureAssessmentLockLaunchFromRouteSnapshot(snapshot: ActivatedRouteSnapshot): void {
    if (snapshot.queryParamMap.has('assessment-lock')) {
      this.assessmentLockLaunchFromLoginQuery = true;
    }
  }

  get isAssessmentLockLaunchFromLoginQuery(): boolean {
    return this.assessmentLockLaunchFromLoginQuery;
  }

  clearAssessmentLockLaunchFromLoginQuery(): void {
    this.assessmentLockLaunchFromLoginQuery = false;
  }

  /**
   * Detect Assessment Lock login semantics before StudentDashboardService.loadAttempt().
   */
  detectAssessmentLockLogin(): boolean {
    return this.alLogin;
  }

  persistAlLoginLocalStorage(): void {
    try {
      localStorage.setItem(this.AL_LOGIN_LOCAL_STORAGE_KEY, '1');
    } catch {
      // ignore quota / private mode
    }
  }

  /**
   * Persisted AL_Login marker (set when {@link AssessmentLockService#alLogin} was true at component initialization).
   */
  hasAlLoginLocalMarker(): boolean {
    try {
      return localStorage.getItem(this.AL_LOGIN_LOCAL_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  /**
   * EQAO PJ: Assessment Lock–aware login (`AL_Login`) — launch query latched on login
   * or kiosk session signalling the Chrome extension.
   */
  get alLogin(): boolean {
    return (
      this.assessmentLockLaunchFromLoginQuery
      || this.auth.activeKioskPassword === 'EXTENSION_DETECTED'
    );
  }

  /** True after {@link init} has been applied (extension ping / listener active). */
  get isExtensionMonitoringInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Get current state snapshot
   */
  get currentState(): AssessmentLockState {
    return this.stateSubject.value;
  }

  /**
   * Check if extension is available and responding
   */
  get isExtensionAvailable(): boolean {
    return this.isFirstPongReceived && !this.isDisallowed;
    // this.currentState.extensionStatus === ExtensionStatus.CONNECTED;
  }

  /**
   * Get tab restoration count (security monitoring)
   */
  get tabRestorationCount(): number {
    return this.currentState.previousTabIds?.length || 0;
  }

  /**
   * Check if suspicious activity detected (multiple tab closures)
   */
  get isSuspiciousActivity(): boolean {
    return this.tabRestorationCount >= 3;
  }

  /**
   * Get current trigger tab ID
   */
  get currentTriggerTabId(): number | null {
    return this.currentState.triggerTabId;
  }

  /**
   * Detect if user is on a Chromebook
   */
  isChromebook(): boolean {
    const userAgent = navigator.userAgent;
    return userAgent.includes('CrOS') || userAgent.includes('Chrome OS');
  }

  /**
   * Log assessment lock issues
   */
  private logAssessmentLockEvent(slug: string, details: any): void {
    if (!this.auth.userIsStudent()) {
      return;
    }
    
    this.auth.apiCreate(this.routes.LOG, {
      slug: slug,
      data: {
        uid: this.auth.getUid(),
        timestamp: new Date().toISOString(),
        isActive: this.isActive,
        extensionStatus: this.currentState.extensionStatus,
        isLockdown: this.currentState.isLockdown,
        ...details
      }
    }).catch(err => {
      console.error('Failed to log assessment lock event:', err);
    });
  }

  /**
   * Initialize message listener for extension signals
   */
  private initializeMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {
      // Security: Only accept messages from same origin
      if (event.origin !== window.location.origin || event.source !== window) {
        return;
      }

      const message = event.data;

      this.ngZone.run(() => {
        switch (message.type) {
          case AsmtRunnerReceive.VRETTA_ASSESSMENT_LOCK_ACTIVATED:
            this.updateState({
              isLockdown: true,
              triggerUrl: message.state?.triggerUrl || window.location.href,
              triggerTabId: message.state?.triggerTabId || null,
              previousTabIds: []  // Reset on activation
            });
            break;

          case AsmtRunnerReceive.VRETTA_ASSESSMENT_LOCK_DEACTIVATED:
            this.updateState({
              isLockdown: false,
              triggerUrl: null,
              triggerTabId: null,
              previousTabIds: []  // Reset on deactivation
            });
            this.isActive = false;
            this.onExitLock();
            break;

          case AsmtRunnerReceive.VRETTA_ASSESSMENT_LOCK_PONG:
            this.handlePong(message);
            break;

          case AsmtRunnerReceive.VRETTA_EXTENSION_CONTEXT_INVALIDATED:
            console.error('Extension context invalidated:', message.reason);
            this.handleContextInvalidated();
            break;

          case AsmtRunnerReceive.VRETTA_VERIFIED_ACCESS_SIGNED:
            this.handleVerifiedAccessSigned(message);
            break;

          case AsmtRunnerReceive.VRETTA_VERIFIED_ACCESS_SIGNING_STATUS_RESPONSE:
            this.handleVerifiedAccessSigningStatusResponse(message);
            break;

          case AsmtRunnerReceive.VRETTA_VERIFIED_ACCESS_FAILED:
            this.handleVerifiedAccessFailed(message);
            break;

          case AsmtRunnerReceive.VRETTA_ASSESSMENT_LOCK_ACTIVATION_FAILED:
            console.error('Activation failed:', message.error);
            this.logAssessmentLockEvent('ASSESSMENT_LOCK_ACTIVATION_FAILED', {
              error: message.error,
              reason: message.reason || 'Unknown',
              triggerUrl: window.location.href
            });
            break;

          case AsmtRunnerReceive.VRETTA_ASSESSMENT_LOCK_DEACTIVATION_FAILED:
            console.error('Deactivation failed:', message.error);
            this.logAssessmentLockEvent('ASSESSMENT_LOCK_DEACTIVATION_FAILED', {
              error: message.error,
              reason: message.reason || 'Unknown',
              triggerUrl: this.currentState.triggerUrl
            });
            break;
        }
      });
    };

    window.addEventListener('message', this.messageListener);
  }

  /**
   * Send ping to extension
   */
  private sendPing(): void {
    window.postMessage({
      type: AsmtRunnerSend.VRETTA_ASSESSMENT_LOCK_PING
    }, '*');

    // Set timeout for no response
    this.pongTimeoutId = window.setTimeout(() => {
      console.warn('No pong received from extension within 5 seconds');
      
      // If lock is active and extension stops responding, log and reload
      if (this.isActive) {
        this.logAssessmentLockEvent('ASSESSMENT_LOCK_EXTENSION_TIMEOUT', {
          reason: 'Extension stopped responding to ping during active lock',
          wasFirstPongReceived: this.isFirstPongReceived,
          triggerUrl: this.currentState.triggerUrl,
          triggerTabId: this.currentState.triggerTabId
        });
        
        console.error('Extension stopped responding during active lock - reloading page');
        window.location.reload();
        return;
      }
      
      this.ngZone.run(() => {
        this.updateState({
          extensionStatus: this.isFirstPongReceived 
            ? ExtensionStatus.NOT_RESPONDING 
            : ExtensionStatus.NOT_INSTALLED
        });
      });
      this.pongTimeoutId = null;
    }, this.PONG_TIMEOUT);
  }

  /**
   * Handle pong response
   */
  private handlePong(message: any): void {
    // Clear timeout
    if (this.pongTimeoutId !== null) {
      clearTimeout(this.pongTimeoutId);
      this.pongTimeoutId = null;
    }

    // Mark first pong received
    if (!this.isFirstPongReceived) {
      this.isFirstPongReceived = true;
      setTimeout(() => {
        console.log('isFirstPongReceived')
      }, 1000)
    }

    const isLockdown = (message.isLockdown || false); 
    
    // Detect unexpected lock deactivation
    if (this.isActive && !isLockdown){
      console.warn('Assessment lock unexpectedly deactivated');
      this.logAssessmentLockEvent('ASSESSMENT_LOCK_UNEXPECTED_DEACTIVATION', {
        reason: 'Lock was active but pong indicates it is no longer active',
        previousTabIds: message.previousTabIds || [],
        triggerTabId: message.triggerTabId || null
      });
      this.onExitLock();
    }
    
    this.isActive = isLockdown

    // Update state with all tracking info
    this.updateState({
      isLockdown: isLockdown,
      triggerTabId: message.triggerTabId || null,
      previousTabIds: message.previousTabIds || [],
      extensionStatus: ExtensionStatus.CONNECTED
    });
    this.verifiedAccessDebug.markExtensionStatus(ExtensionStatus.CONNECTED, this.isExtensionAvailable);
  }

  /**
   * Handle context invalidation
   */
  private handleContextInvalidated(): void {
    console.error('Extension context invalidated');
    
    this.updateState({
      extensionStatus: ExtensionStatus.CONTEXT_INVALIDATED
    });
    
    // Log the context invalidation, especially critical if lock is active
    this.logAssessmentLockEvent('ASSESSMENT_LOCK_CONTEXT_INVALIDATED', {
      reason: 'Extension context was invalidated',
      triggerUrl: this.currentState.triggerUrl,
      triggerTabId: this.currentState.triggerTabId,
      willReload: this.isActive
    });
    
    this.stopPingInterval();
    
    // Reload page if lock is active to restore proper state
    if (this.isActive) {
      console.error('Extension context invalidated during active lock - reloading page');
      // Small delay to ensure log is sent
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }

  /**
   * Verfied Access / managed kiosk trust:
   * Proves whether a student is on a managed ChromeOS kiosk session
   */
  async prepareDeviceTrust(test_session_id: number | string): Promise<{ device_trust_token: string, trust_mode: string } | null> {
    // 0. Reuse unexpired trust token if student is navigating between sub-routes in the same test session
    const reusableDeviceTrust = this.getReusableDeviceTrust(test_session_id);
    if (reusableDeviceTrust) {
      this.verifiedAccessDebug.markReusedManagedKioskTrust(
        test_session_id,
        this.currentState.extensionStatus,
        this.isExtensionAvailable,
        reusableDeviceTrust.device_trust_token,
        reusableDeviceTrust.trust_mode,
        reusableDeviceTrust.expires_in
      );
      return {
        device_trust_token: reusableDeviceTrust.device_trust_token,
        trust_mode: reusableDeviceTrust.trust_mode
      };
    }
    this.verifiedAccessDebug.resetForAttestation(test_session_id);
    this.verifiedAccessDebug.markExtensionStatus(this.currentState.extensionStatus, this.isExtensionAvailable);
    // first check if the extension is active, fail early if not
    if (!this.isExtensionAvailable) {
      this.verifiedAccessDebug.markExtensionUnavailable();
      throw new Error('VERIFIED_ACCESS_EXTENSION_UNAVAILABLE');
    }

    // 1. Preflight: extension checks whether `enterprise.platformKeys.challengeKey` is available on the device before triggering challenge generation
    try {
      const signingStatus = await this.requestVerifiedAccessSigningStatus();
      this.verifiedAccessDebug.markSigningStatusSuccess(signingStatus);
      // do not generate a challenge if challenge function doesnt exist on device
      if (signingStatus.challengeKeyAvailable === false) {
        this.verifiedAccessDebug.markSigningUnavailable(signingStatus.error);
        throw new Error('VERIFIED_ACCESS_SIGNING_UNAVAILABLE');
      }
    } catch (err) {
      if (err?.message === 'VERIFIED_ACCESS_SIGNING_UNAVAILABLE') {
        throw err;
      }
      this.verifiedAccessDebug.markSigningStatusUnknown(err);
    }

    // 2. Create attestation challenge: API generates a challenge with Google
    let attestation;
    try {
      this.verifiedAccessDebug.markChallengeCreatePending();
      attestation = await this.auth.apiCreate(
        this.routes.STUDENT_VERIFIED_ACCESS_ATTESTATION,
        { test_session_id },
        this.verifiedAccessDebug.isEnabled() ? { query: { debug: 1 } } : undefined
      );
      this.verifiedAccessDebug.markChallengeCreated(attestation);
    } catch (err) {
      this.verifiedAccessDebug.markChallengeCreateFailed(err);
      throw err;
    }

    // 3. Extension signing: send the generated challenge to the extension for signature generation
    let challengeResponse;
    try {
      this.verifiedAccessDebug.markExtensionSignPending();
      challengeResponse = await this.requestVerifiedAccessSignature(attestation.attestation_id, attestation.challenge);
      this.verifiedAccessDebug.markExtensionSigned(challengeResponse);
    } catch (err) {
      this.verifiedAccessDebug.markExtensionSignFailed(err);
      throw err;
    }

    // 4. Verify challenge: send the signed challenge response to the API to verify against Google's Verified Access service
    let verified;
    let deviceTrustTokenIssuedAtMs = 0;
    try {
      this.verifiedAccessDebug.markChallengeVerifyPending();
      verified = await this.auth.apiPatch(
        this.routes.STUDENT_VERIFIED_ACCESS_ATTESTATION,
        attestation.attestation_id,
        {
          attestation_token: attestation.attestation_token,
          challengeResponse
        },
        this.verifiedAccessDebug.isEnabled() ? { query: { debug: 1 } } : undefined
      );
      // google verification failure (403 permission_denied) is an expected negative result: clear any cached trust and use extension fallback
      if (verified.verified === false) {
        this.clearDeviceTrust();
        
        this.verifiedAccessDebug.markChallengeVerifyFailed({
          message: verified.reason || 'Verification completed without generating a device trust token.'
        });
        return null;
      }
      deviceTrustTokenIssuedAtMs = Date.now();
      this.verifiedAccessDebug.markChallengeVerified(verified, deviceTrustTokenIssuedAtMs, {
        ...(attestation.debug || {}),
        ...(verified.debug || {})
      });
    } catch (err) {
      this.verifiedAccessDebug.markChallengeVerifyFailed(err);
      throw err;
    }

    // 5. Cache trust proof in memory: store returned device trust token and expiry timestamp so route changes do not trigger another Google attestation immediately
    this.deviceTrustToken = verified.device_trust_token;
    this.deviceTrustMode = verified.trust_mode;
    this.deviceTrustTestSessionId = test_session_id;
    this.deviceTrustUid = this.auth.getUid();
    this.deviceTrustTokenIssuedAtMs = deviceTrustTokenIssuedAtMs;
    this.deviceTrustTokenExpiresAtMs = this.deviceTrustTokenIssuedAtMs + ((verified.expires_in || 0) * 1000);
    return {
      device_trust_token: verified.device_trust_token,
      trust_mode: verified.trust_mode
    };
  }

  get currentDeviceTrustToken(): string | null {
    return this.deviceTrustToken;
  }
  get currentDeviceTrustMode(): string | null {
    return this.deviceTrustMode;
  }
  // clear cached trust state on logout or AL deactivation
  clearDeviceTrust(): void {
    this.deviceTrustToken = null;
    this.deviceTrustMode = null;
    this.deviceTrustTestSessionId = null;
    this.deviceTrustUid = null;
    this.deviceTrustTokenIssuedAtMs = null;
    this.deviceTrustTokenExpiresAtMs = null;
  }

  // returns the current managed kiosk token only when it is still valid for the same test session and not close to expiry
  private getReusableDeviceTrust(test_session_id: number | string): { device_trust_token: string, trust_mode: string, expires_in: number } | null {
    // Only managed kiosk attestations are reusable
    if (!this.deviceTrustToken || this.deviceTrustMode !== 'managed_kiosk') {
      return null;
    }
    // Do not share trust tokens across different test sessions
    if (`${this.deviceTrustTestSessionId}` !== `${test_session_id}`) {
      return null;
    }
    // Check uid before deciding to reuse a valid token
    if (this.deviceTrustUid !== this.auth.getUid()) {
      this.clearDeviceTrust();
      return null;
    }
    if (!this.deviceTrustTokenExpiresAtMs) {
      return null;
    }
    // Check remaining TTL against safety buffer
    const expiresInSeconds = Math.floor((this.deviceTrustTokenExpiresAtMs - Date.now()) / 1000);
    if (expiresInSeconds <= this.DEVICE_TRUST_REUSE_BUFFER_SECONDS) {
      return null;
    }

    return {
      device_trust_token: this.deviceTrustToken,
      trust_mode: this.deviceTrustMode,
      expires_in: expiresInSeconds
    };
  }

  // Posts challenge to extension window and sets a 15s timeout for signature generation
  private requestVerifiedAccessSignature(attestation_id: string, challenge: string): Promise<string> {
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    return new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        this.verifiedAccessRequests.delete(requestId);
        reject(new Error('VERIFIED_ACCESS_EXTENSION_TIMEOUT'));
      }, 15000);

      this.verifiedAccessRequests.set(requestId, { resolve, reject, timeoutId });

      window.postMessage({
        type: AsmtRunnerSend.VRETTA_VERIFIED_ACCESS_SIGN_REQUEST,
        requestId,
        attestation_id,
        challenge
      }, '*');
    });
  }

  // Extension checks whether challenge signing is available before creating a challenge, uses a 1.5s timeout
  private requestVerifiedAccessSigningStatus(): Promise<any> {
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    return new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        this.verifiedAccessSigningStatusRequests.delete(requestId);
        this.verifiedAccessDebug.markSigningStatusTimeout();
        reject(new Error('VERIFIED_ACCESS_SIGNING_STATUS_TIMEOUT'));
      }, this.VERIFIED_ACCESS_SIGNING_STATUS_TIMEOUT);

      this.verifiedAccessSigningStatusRequests.set(requestId, { resolve, reject, timeoutId });

      window.postMessage({
        type: AsmtRunnerSend.VRETTA_VERIFIED_ACCESS_SIGNING_STATUS_REQUEST,
        requestId
      }, '*');
    });
  }

  // Match the extensions response to the pending requestID and resolve promise
  private handleVerifiedAccessSigningStatusResponse(message: any): void {
    const pendingRequest = this.verifiedAccessSigningStatusRequests.get(message.requestId);
    if (!pendingRequest) {
      return;
    }

    clearTimeout(pendingRequest.timeoutId);
    this.verifiedAccessSigningStatusRequests.delete(message.requestId);
    pendingRequest.resolve(message);
  }

  private handleVerifiedAccessSigned(message: any): void {
    const pendingRequest = this.verifiedAccessRequests.get(message.requestId);
    if (!pendingRequest) {
      return;
    }

    clearTimeout(pendingRequest.timeoutId);
    this.verifiedAccessRequests.delete(message.requestId);
    pendingRequest.resolve(message.challengeResponse);
  }

  // Reject the pending signing request
  private handleVerifiedAccessFailed(message: any): void {
    const pendingRequest = this.verifiedAccessRequests.get(message.requestId);
    if (!pendingRequest) {
      return;
    }

    clearTimeout(pendingRequest.timeoutId);
    this.verifiedAccessRequests.delete(message.requestId);
    pendingRequest.reject(new Error(message.error || 'VERIFIED_ACCESS_EXTENSION_FAILED'));
  }

  /**
   * Start ping interval
   */
  private startPingInterval(): void {
    // Send initial ping
    this.sendPing();

    // Send ping every 30 seconds
    this.pingIntervalId = window.setInterval(() => {
      this.sendPing();
    }, this.PING_INTERVAL);
  }

  /**
   * Stop ping interval
   */
  private stopPingInterval(): void {
    if (this.pingIntervalId !== null) {
      clearInterval(this.pingIntervalId);
      this.pingIntervalId = null;
    }

    if (this.pongTimeoutId !== null) {
      clearTimeout(this.pongTimeoutId);
      this.pongTimeoutId = null;
    }
  }

  /**
   * Update state (partial update)
   */
  private updateState(partial: Partial<AssessmentLockState>): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      ...partial
    });
  }

  /**
   * Activate the assessment lock extension.
   * @param callback Optional callback invoked when the lock exits.
   * @param assessmentLanguage Optional language code (e.g. 'en', 'fr'). When provided,
   *   the extension uses it for in-extension UI (exit popup, prompts). When absent the
   *   extension falls back to its default (English), keeping ABED and other clients working.
   */
  activateLock(callback?: () => any, opts?: { blockExtensions?: boolean, extensionAllowlist?: string[] }, assessmentLanguage?: string): void {
    const message: Record<string, unknown> = {
      type: AsmtRunnerSend.VRETTA_ACTIVATE_ASSESSMENT_LOCK,
      url: window.location.href,
      supervisorPassword: this.supervisorPassword,
    };
    // Extension blocking -> needs to be explictly enabled
    if (opts?.blockExtensions === true) {
      message.blockExtensions = true;
    }

    this.logAssessmentLockEvent('ASSESSMENT_LOCK_EXTENSION_BLOCKING_REQUESTED', {
      requested: opts?.blockExtensions === true,
      requestUrl: message.url,
    });

        // Extension allow list (only when student has accommodation)
    if (opts?.extensionAllowlist){
      message.extensionAllowlist = opts?.extensionAllowlist;
    }

    this.logAssessmentLockEvent('ASSESSMENT_LOCK_EXTENSION_ALLOWLIST_REQUESTED', {
      allowList: opts?.extensionAllowlist,
      requestUrl: message.url,
    });

    if (assessmentLanguage != null && assessmentLanguage !== '') {
      message.assessmentLanguage = assessmentLanguage;
    }
    window.postMessage(message, '*');
    this.isActive = true;
    this.onExitLock = () => callback(); // todo: should use a sub
  }

  onExitLock: () => any;

  /**
   * Notify the extension of the assessment language so all in-extension UI
   * (exit popup, prompts) uses it. No-op when the value is missing or not a string,
   * so ABED and other clients that never call this remain unaffected.
   */
  setAssessmentLanguage(assessmentLanguage: string): void {
    if (assessmentLanguage && typeof assessmentLanguage === 'string') {
      window.postMessage(
        {
          type: AsmtRunnerSend.VRETTA_ASSESSMENT_LOCK_SET_LANGUAGE,
          language: assessmentLanguage,
        },
        '*',
      );
    }
  }

  /**
   * Deactivate assessment lock
   * Note: Extension will prompt for supervisor password if EXIT_PASSWORD_PROMPT is enabled
   */
  deactivateLock(callback?: () => any): void {
    window.postMessage({
      type: AsmtRunnerSend.VRETTA_DEACTIVATE_ASSESSMENT_LOCK
    }, '*');
    // this.isActive = false; // ?
    if(callback) {
      this.onExitLock = callback;
    }
  }

  deactivateLockForced(): void {
    window.postMessage({
      type: AsmtRunnerSend.VRETTA_DEACTIVATE_ASSESSMENT_LOCK,
      isSkipPasswordPrompt: true,
      supervisorPassword: this.supervisorPassword
    }, '*');
    // this.isActive = false; // ?
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.stopPingInterval();
    
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
  }
}
